import isNull from "lodash-es/isNull";
import React from "react";

const defaultEmptyCheckFn: (input: string | null) => input is null = isNull;

const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
  emptyCheckFn = defaultEmptyCheckFn,
) => {
  const [state, setState] = React.useState<T | undefined>(() => {
    const current = getLocalStorageItem(key);

    if (!emptyCheckFn(current)) {
      try {
        return JSON.parse(current);
      } catch {
        removeLocalStorageItem(key);
      }
    }

    return initialValue;
  });

  const deleteState = () => {
    setState(undefined);
  };

  React.useEffect(() => {
    if (state === null || state === undefined) {
      removeLocalStorageItem(key);
    } else {
      setLocalStorageItem(key, state);
    }
  }, [key, state]);

  return [state, setState, deleteState] as const;
};

const setLocalStorageItem = (key: string, value: unknown) => {
  const stringified = JSON.stringify(value);
  window.localStorage.setItem(key, stringified);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
};

const getLocalStorageItem = (key: string) => window.localStorage.getItem(key);

const arrayEmptyCheckFn = (input: string | null): input is null =>
  isNull(input) || input === "[]";

export { arrayEmptyCheckFn, useLocalStorage };
